<template>
  <div class="container">
    <div v-if="showLoggingIn" class="left">
      <router-link to="/">
        <img
          src="@/assets/images/symbol_blue.png"
          alt="Company Logo"
          class="company-logo"
        />
      </router-link>
      <div class="loading-icon mb-2">
        <QSLoading :isLoading="showLoggingIn" :size="65" :colorTheme="true" />
      </div>
      <h4 style="font-weight: 300;">
        {{ currentMessage }}
      </h4>
    </div>
    <div v-if="!showLoggingIn" class="left">
      <router-link to="/">
        <img
          src="@/assets/images/symbol_blue.png"
          alt="Company Logo"
          class="company-logo"
        />
      </router-link>

      <div class="title">
        <slot name="title"></slot>
      </div>
      <span v-if="valError">
        <p
          style="text-align: center; color: var(--red); font-size: 1.00rem; font-weight: 300;"
        >
          {{ errorMessage }}
        </p>
      </span>
      <span v-if="valSuccess">
        <p
          style="text-align: center; color: var(--green); font-size: 1.00rem; font-weight: 300;"
        >
          {{ successMessage }}
        </p>
      </span>

      <form @submit.prevent="$emit('submit')">
        <slot name="form-fields"></slot>
        <button
          v-if="showButton"
          type="submit"
          :disabled="loggingIn"
          @click="$emit('button-click')"
        >
          <slot name="button-text"></slot>
          <span v-if="showLoggingIn" class="text-left ml-2">
            <QSLoading
              :isLoading="showLoggingIn"
              :size="20"
              :colorTheme="true"
            />
          </span>
        </button>
      </form>

      <div v-if="showSocialLogins && actionText" class="separator">
        Or, {{ actionText }} with
      </div>

      <div
        v-if="showSocialLogins"
        class="social-login-buttons"
        style="position: relative;"
      >
        <!-- Google Sign-In -->
        <div
          id="g_id_onload"
          data-client_id="260935068115-opv140318u68ce7odt1j29gth2p4mrlg.apps.googleusercontent.com"
          :data-callback="googleCallback"
          data-auto_prompt="false"
        ></div>
        <div
          class="social-login-btn google-login-btn"
          ref="googleButton"
          @mouseenter="hoverLogins('Google')"
          @mouseleave="hoverLogins(null)"
        ></div>

        <!-- Microsoft Sign-In -->
        <div
          style="margin: 0 15px 0 15px"
          class="social-login-btn microsoft-login-btn"
          @click="$emit('microsoft-login')"
          @mouseenter="hoverLogins('Microsoft')"
          @mouseleave="hoverLogins(null)"
        >
          <img
            src="https://docs.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-azure-ad-apps/ms-symbollockup_mssymbol_19.png"
            alt="Microsoft Logo"
            class="social-logo"
          />
        </div>

        <!-- Safenet Sign-In -->
        <div
          class="social-login-btn safenet-login-btn"
          @click="$emit('safenet-login')"
          @mouseenter="hoverLogins('Safenet')"
          @mouseleave="hoverLogins(null)"
        >
          <img
            src="https://companieslogo.com/img/orig/HO.PA-99d27d4e.png?t=1720244492"
            alt="Safenet Logo"
            class="social-logo"
          />
        </div>

        <div v-if="showHoverMessage" class="hoverDiv">
          {{ hoverMessage }}
        </div>
      </div>
    </div>
    <div class="right">
      <div class="right-text">
        <h1>
          <span class="typing">{{ typedText }}</span>
        </h1>
        <h3 v-if="showHeadings" class="fade-in">
          Protect your company from
        </h3>
        <h3 v-if="showHeadings" class="fade-in">market volatility</h3>
      </div>
      <img
        src="@/assets/images/carousel1QS.jpg"
        alt="Background Image"
        class="static-image"
      />
    </div>
  </div>
</template>

<script>
import QSLoading from "@/components/QSLoading.vue";

export default {
  components: { QSLoading },
  data() {
    return {
      currentMessageIndex: 0,
      fullText: "Inspiring better hedging decisions",
      hoverMessage: "",
      isLoggingIn: false,
      messageInterval: null,
      messages: [
        "Validating credentials...",
        "Setting up your profile...",
        "Retrieving market data..."
      ],
      msalInstance: null,
      screenWidth: 10,
      showHeadings: false,
      showHoverMessage: false,
      tooltip: null,
      typedText: "",
      typingSpeed: 100
    };
  },
  props: {
    actionText: { type: String, default: "" },
    errorMessage: { type: String, default: "" },
    googleCallback: { type: String, default: "handleGoogleLogin" },
    linkLabel: { type: String, default: "" },
    linkText: { type: String, default: "" },
    linkTo: { type: String, default: "" },
    loggingIn: { type: Boolean, default: false },
    showButton: { type: Boolean, default: true },
    showLoggingIn: { type: Boolean, default: false },
    showSocialLogins: { type: Boolean, default: true },
    successMessage: { type: String, default: "" },
    valError: { type: Boolean, default: false },
    valSuccess: { type: Boolean, default: false }
  },
  watch: {
    showLoggingIn(newVal) {
      if (newVal) {
        this.startMessageRotation();
      } else {
        this.stopMessageRotation();
      }
    }
  },
  mounted() {
    if (this.showSocialLogins) {
      this.loadGoogleSignIn();
      this.screenWidth = window.innerWidth;
    }
    setTimeout(() => {
      this.animateText();
      setTimeout(() => {
        this.showHeadings = true;
      }, 500);
    }, 1000);
    if (this.showLoggingIn) {
      this.startMessageRotation();
    }
  },
  beforeDestroy() {
    this.stopMessageRotation();
  },
  methods: {
    hoverLogins(msg) {
      if (msg) {
        this.hoverMessage = msg;
        this.showHoverMessage = true;
      } else {
        this.hoverMessage = "";
        this.showHoverMessage = false;
      }
    },
    loadGoogleSignIn() {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window[this.googleCallback] = this.$parent.handleGoogleLogin;
        google.accounts.id.initialize({
          client_id:
            "260935068115-opv140318u68ce7odt1j29gth2p4mrlg.apps.googleusercontent.com",
          callback: this.$parent.handleGoogleLogin
        });
        google.accounts.id.renderButton(this.$refs.googleButton, {
          theme: "outline",
          size: "large",
          width: "50px",
          height: "50px",
          type: "icon"
        });
      };
      document.head.appendChild(script);
    },
    animateText() {
      let index = 0;
      const interval = setInterval(() => {
        if (index < this.fullText.length) {
          this.typedText += this.fullText[index];
          index++;
        } else {
          clearInterval(interval);
        }
      }, this.typingSpeed);
    },
    startMessageRotation() {
      this.currentMessageIndex = 0;
      this.messageInterval = setInterval(() => {
        this.currentMessageIndex =
          (this.currentMessageIndex + 1) % this.messages.length;
      }, 2000);
    },
    stopMessageRotation() {
      if (this.messageInterval) {
        clearInterval(this.messageInterval);
        this.messageInterval = null;
      }
    }
  },
  computed: {
    currentMessage() {
      return this.messages[this.currentMessageIndex];
    }
  }
};
</script>

<style scoped>
.hoverDiv {
  color: var(--textactive-2);
  position: absolute;
  text-align: center;
  top: 55px;
  z-index: 1;
  width: 100px;
}

.container {
  display: flex;
  width: 100vw;
  height: 100vh;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0;
}

.left {
  width: 45%;
  height: 100%;
  background-color: black;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: var(--textactive-2);
  padding: 20px 0;
  position: relative;
}

.company-logo {
  position: absolute;
  top: 5%;
  left: 5%;
  width: 6%;
  height: auto;
  cursor: pointer;
}

.right {
  width: 55%;
  height: 100%;
  background-color: var(--darkbackgroundFixed);
  position: relative;
  overflow: hidden;
  min-width: 0;
  flex-shrink: 1;
}

.right-text {
  position: absolute;
  top: 8%;
}

.right h1 {
  color: var(--textactive-2);
  font-size: 400%;
  margin-bottom: 3%;
}

.right h3 {
  color: var(--textInactiveFixed);
  font-size: 250%;
  font-weight: 300;
}

.title {
  text-align: left;
  width: 80%;
  max-width: 400px;
  font-weight: 300;
}

.title p {
  text-align: left;
  font-size: 16px;
  color: var(--textactive-2);
  margin-top: 3%;
  line-height: 1.3;
}

h2 {
  font-size: 36px;
  margin: 0;
  line-height: 1.1;
  color: var(--textInactiveFixed);
  font-weight: normal;
}

form {
  display: flex;
  flex-direction: column;
  width: 80%;
  max-width: 400px;
}

label {
  margin-top: 15px;
  font-size: 14px;
  font-weight: bold;
}

input {
  padding: 10px;
  margin-top: 5px;
  border: 1px solid var(--textactive-2);
  border-radius: 5px;
  width: 100%;
  color: var(--textinactive-2);
}

input::placeholder {
  color: var(--textInactiveFixed);
}

button {
  background-color: var(--expo);
  color: var(--textactive-2);
  padding: 12px;
  border: none;
  border-radius: 5px;
  margin-top: 20px;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: var(--expo-alpha);
}

.separator {
  text-align: center;
  margin-top: 30px;
  margin-bottom: 15px;
  font-size: 12px;
  color: var(--textInactiveFixed);
}

.register {
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
}

.register a {
  color: var(--symmetry-h);
  text-decoration: none;
  font-weight: bold;
  transition: color 0.3s ease;
}

.register a:hover {
  color: var(--expo);
}

.typing {
  border-right: 2px solid var(--white);
  padding-right: 2px;
  animation: blink 0.8s infinite;
}

.social-login-buttons {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}

.social-login-btn {
  width: 50px; /* Square button size */
  height: 50px;
  background-color: var(--white);
  border: 1px solid var(--black);
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.social-login-btn:hover {
  background-color: var(--white);
}

.social-logo {
  width: 30px; /* Adjust logo size to fit within the square */
  height: 30px;
  object-fit: contain; /* Ensure logo scales properly */
}

.microsoft-login-btn {
  /* Ensure consistency with other buttons */
}

.google-login-btn {
  /* Google button will be styled by the API, but container size is controlled here */
}

.safenet-login-btn {
  /* Add any Safenet-specific styles if needed */
}

@keyframes blink {
  50% {
    border-color: transparent;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.static-image {
  width: 100%;
  height: 100%;
  animation: slideUp 1s ease-out;
}

.fade-in {
  opacity: 0;
  animation: fadeIn 1s ease-in forwards;
}

.loading-icon {
  display: flex;
  justify-content: center;
  max-height: 20%;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
